/**
 * ProtectedRoute — backward-compat shim that delegates to the new RBAC system.
 *
 * Auth detection rule (Session 4 hardening):
 *   - "Authenticated" = there is an `accessToken` in localStorage.
 *     If `useAccess` fails (network/500), we DO NOT redirect to /login —
 *     that would create a redirect loop right after a successful login when
 *     the access endpoint is temporarily unavailable.
 *   - The access snapshot from `useAccess` only governs PERMISSION decisions,
 *     not authentication state.
 *
 * Behavior:
 *   - No accessToken → /login.
 *   - Loading first snapshot → spinner.
 *   - Snapshot ready + permission missing → /403.
 *   - Snapshot ready + permission OK → render children.
 *   - Snapshot errored → render children with a console warning so the user
 *     isn't kicked out (the backend will still independently enforce the
 *     permission on every API call).
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAccess } from '../../hooks/useAccess';
import { findLeafByPath } from '../../constants/adminMenus';
import { PERMISSION_CATALOG, type PermissionCode, type PermissionMeta } from '../../constants/permissionCatalog';

interface ProtectedRouteProps {
  requiredPermissions?: string | string[];
  children: React.ReactNode;
}

const hasAccessToken = (): boolean => {
  try {
    return !!localStorage.getItem('accessToken');
  } catch {
    return false;
  }
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermissions,
  children,
}) => {
  const location = useLocation();
  const { has, isLoading, user, error, shift } = useAccess();

  // 1) Definitive auth check — no token, no entry.
  if (!hasAccessToken()) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname)}`}
        state={{ from: location }}
        replace
      />
    );
  }

  // 2) Wait for the first access snapshot.
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // 3) If /admin/me/access errored, render the page anyway. The user has a
  //    valid token — kicking them to /login causes a redirect loop. Each API
  //    call still enforces the real permission on the server side.
  if (error || !user.id) {
    // eslint-disable-next-line no-console
    console.warn(
      '[ProtectedRoute] access snapshot unavailable — rendering anyway. ' +
        'Backend will enforce per-request permission.',
      { error, path: location.pathname },
    );
    return <>{children}</>;
  }

  // 4) Resolve required permissions.
  let required: string[] = [];
  if (Array.isArray(requiredPermissions)) {
    required = requiredPermissions;
  } else if (requiredPermissions) {
    required = [requiredPermissions];
  } else {
    // Try exact path match first; fall back to parent paths for dynamic routes
    // (e.g. `/tickets/:id` resolves via `/tickets` leaf).
    let leaf = findLeafByPath(location.pathname);
    if (!leaf) {
      const parts = location.pathname.split('/').filter(Boolean);
      while (!leaf && parts.length > 1) {
        parts.pop();
        leaf = findLeafByPath('/' + parts.join('/'));
      }
    }
    if (leaf) {
      // `permission: null` marks a self-scoped page (its endpoint can only
      // return the caller's own data) — open to every authenticated admin.
      if (leaf.permission === null) return <>{children}</>;
      required = Array.isArray(leaf.permission) ? leaf.permission : [leaf.permission];
    }
  }

  if (required.length === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[ProtectedRoute] No permission gate for "${location.pathname}". ` +
        'Add the route to adminMenus.ts or pass requiredPermissions explicitly. ' +
        'Falling through to backend enforcement.',
    );
    return <>{children}</>;
  }
  if (has(required as PermissionCode[])) return <>{children}</>;

  // Distinguish "you simply don't have access" from "you DO handle this section but
  // you're off-shift right now". If any required permission belongs to a work-category
  // that the user is assigned to (shift.categories) and they're not on-shift, it's the
  // shift window — not a missing role. Surface the clearer off_shift message.
  const catalog = PERMISSION_CATALOG as Record<string, PermissionMeta>;
  const handlesCategoryButOffShift =
    shift.status !== 'on_shift' &&
    !user.isSuperAdmin &&
    required.some((p) => {
      const cat = catalog[p]?.category;
      return cat && shift.categories.includes(cat);
    });

  const reason = handlesCategoryButOffShift ? 'off_shift' : 'no_permission';

  return (
    <Navigate
      to={`/403?reason=${reason}&perm=${encodeURIComponent(required.join(','))}`}
      state={{ from: location.pathname }}
      replace
    />
  );
};

export default ProtectedRoute;
